import type { Edulead } from 'edulead';
import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { allPages, toastV4Error } from '../../services/edulead-v4';
import { eduleadV4 } from '../../services/edulead-v4-client';
import { hasTerritoryEdits, useNavigatorStore } from '../../zustand/useNavigatorStore';
import { TerritoryDetail } from './TerritoryDetail';
import { TerritoryList } from './TerritoryList';
import './NavigatorPanel.scss';
import './NavigatorBeaconsPanel.scss';

/** The URL's query parameters: the selected territories' ids (comma separated), and whether the
 * one selected territory's form is open. */
const TERRITORY_PARAM = 'territory';
const EDIT_PARAM = 'edit';

/**
 * The Navigator's right panel in Territories mode, as the Beacons mode's: the team's territories,
 * and one territory's form. A click on a territory selects it (?territory=<id>) and the map fills
 * its states in its color; a Cmd/Ctrl+click adds it to the selection or takes it out, the map
 * filling them all. A territory selected alone is the one the map edits: a click on a state adds
 * or removes it, staged until Save. Its edit button opens it (&edit=1), sliding the form in.
 */
export function NavigatorTerritoriesPanel() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [territories, setTerritories] = useState<Edulead.EduleadV4Territory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  // The territory shown in the form; kept while it slides out, so the form does not empty.
  const [shownId, setShownId] = useState<string | null>(null);
  const editTerritory = useNavigatorStore((state) => state.editTerritory);
  const setTerritoryFills = useNavigatorStore((state) => state.setTerritoryFills);
  const editingTerritoryId = useNavigatorStore((state) => state.editingTerritoryId);
  const editedStates = useNavigatorStore((state) => state.territoryStates);
  const savedStates = useNavigatorStore((state) => state.territorySavedStates);
  const isDirty = useNavigatorStore(hasTerritoryEdits);
  const undo = useNavigatorStore((state) => state.undoTerritoryEdit);
  const redo = useNavigatorStore((state) => state.redoTerritoryEdit);

  // The URL's territories, those the list has (a deleted one's link drops it).
  const param = searchParams.get(TERRITORY_PARAM) ?? '';
  const selectedIds = param
    .split(',')
    .filter((id, i, all) => id && all.indexOf(id) === i && territories.some((t) => t.id === id));
  const selectedKey = selectedIds.join(',');
  const selectedId = selectedIds.length === 1 ? selectedIds[0] : null;
  const openedId = selectedId && searchParams.get(EDIT_PARAM) === '1' ? selectedId : null;

  useEffect(() => {
    loadTerritories();
  }, []);

  useEffect(() => {
    if (openedId) setShownId(openedId);
  }, [openedId]);

  const loadTerritories = async () => {
    try {
      setIsLoading(true);
      // A team holds at most 50 territories: one page.
      const all = await allPages(eduleadV4.territories.list({ page_size: 50 }));
      setTerritories(all.sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')));
    } catch (error) {
      toastV4Error(error, 'Failed to load territories');
      setTerritories([]);
    } finally {
      setIsLoading(false);
    }
  };

  // A territory selected alone is the one the map edits, from the states the API holds.
  useEffect(() => {
    const territory = territories.find((t) => t.id === selectedId);
    editTerritory(territory?.id ?? null, territory?.states ?? null);
  }, [selectedId, isLoading]);

  // What a save left the edited territory holding goes into the list.
  useEffect(() => {
    if (!editingTerritoryId || !savedStates) return;
    setTerritories((prev) =>
      prev.map((t) =>
        t.id === editingTerritoryId
          ? { ...t, states: savedStates as Edulead.EduleadV4Territory.States.Item[] }
          : t,
      ),
    );
  }, [savedStates]);

  // The map fills the selected territories' states, the edited one's as edited, each in its
  // color; the first selected wins a state they share.
  useEffect(() => {
    const fills: Record<string, string> = {};
    selectedIds.forEach((id) => {
      const territory = territories.find((t) => t.id === id);
      const states = id === editingTerritoryId ? editedStates : (territory?.states ?? []);
      states.forEach((code) => {
        if (!fills[code]) fills[code] = `--territory-color-${territory?.color ?? 1}`;
      });
    });
    setTerritoryFills(fills);
  }, [selectedKey, territories, editingTerritoryId, editedStates]);

  // Cmd/Ctrl+Z undoes and Shift+Cmd/Ctrl+Z redoes while a territory is edited, except in a field.
  useEffect(() => {
    if (!editingTerritoryId) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'z') return;
      const el = e.target as HTMLElement | null;
      if (el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable))
        return;
      e.preventDefault();
      if (e.shiftKey) redo();
      else undo();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [editingTerritoryId]);

  // Leaving Territories mode takes the fills and the edited territory with it.
  useEffect(
    () => () => {
      setTerritoryFills({});
      editTerritory(null, null);
    },
    [],
  );

  /**
   * Selects a territory alone, or none with a click on the one selected; additive (Cmd/Ctrl+click)
   * adds it to the selection or takes it out. Edits not saved are dropped, after a word.
   */
  const select = (id: string, isAdditive: boolean) => {
    if (useNavigatorStore.getState().isSavingTerritory) return;
    if (isDirty && !window.confirm('This territory has unsaved changes. Discard them?')) return;
    const ids = isAdditive
      ? selectedIds.includes(id)
        ? selectedIds.filter((s) => s !== id)
        : [...selectedIds, id]
      : selectedKey === id
        ? []
        : [id];
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (ids.length > 0) next.set(TERRITORY_PARAM, ids.join(','));
      else next.delete(TERRITORY_PARAM);
      next.delete(EDIT_PARAM);
      return next;
    });
  };

  const open = (id: string) => {
    if (
      id !== selectedId &&
      isDirty &&
      !window.confirm('This territory has unsaved changes. Discard them?')
    )
      return;
    setShownId(id);
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set(TERRITORY_PARAM, id);
      next.set(EDIT_PARAM, '1');
      return next;
    });
  };

  /** Back from a territory's form: the territory stays selected, its edits staged. */
  const close = () => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete(EDIT_PARAM);
      return next;
    });
  };

  /** Puts a rename's returned territory in place of the listed one, keeping its states. */
  const replaceTerritory = (territory: Edulead.EduleadV4Territory) => {
    setTerritories((prev) =>
      prev.map((t) => (t.id === territory.id ? { ...territory, states: t.states } : t)),
    );
  };

  const shownTerritory = shownId ? (territories.find((t) => t.id === shownId) ?? null) : null;

  return (
    <div className="navigator-panel navigator-beacons-panel">
      <div
        className={`navigator-beacons-panel__track ${openedId ? 'navigator-beacons-panel__track--shifted' : ''}`}
      >
        <div
          className="navigator-beacons-panel__pane navigator-beacons-panel__pane--list"
          aria-hidden={openedId !== null}
        >
          <div className="navigator-beacons-panel__list">
            <TerritoryList
              territories={territories}
              isLoading={isLoading}
              onOpen={open}
              onSelect={select}
              selectedIds={selectedIds}
              onCreated={(territory) =>
                setTerritories((prev) =>
                  [...prev, territory].sort((a, b) => (a.name ?? '').localeCompare(b.name ?? '')),
                )
              }
              onDeleted={(id) => setTerritories((prev) => prev.filter((t) => t.id !== id))}
            />
          </div>
        </div>
        <div
          className="navigator-beacons-panel__pane navigator-beacons-panel__pane--form"
          aria-hidden={openedId === null}
        >
          {shownTerritory?.id && (
            <TerritoryDetail
              key={shownTerritory.id}
              territory={shownTerritory}
              isOpen={openedId === shownTerritory.id}
              onBack={close}
              onChanged={replaceTerritory}
            />
          )}
        </div>
      </div>
    </div>
  );
}
